/**
 * useVisitorToken — anonymous browser ID stable across visits.
 *
 * Generates a UUID v4 the first time a visitor opens the page and stores
 * it in localStorage so the backend can recognise them on return visits
 * (powering the "Saturday morning re-mix" loop — fresh memories first).
 *
 * Pure browser-state hook — never sent to a server until the caller
 * opts in by passing it as a param/body field.
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "reminissing.visitor_token";

function generate() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    // Fallback for older browsers — RFC 4122-ish.
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

export function useVisitorToken() {
    const [token, setToken] = useState(null);
    useEffect(() => {
        try {
            let t = localStorage.getItem(STORAGE_KEY);
            if (!t || t.length < 16) {
                t = generate();
                localStorage.setItem(STORAGE_KEY, t);
            }
            setToken(t);
        } catch (_e) {
            // Private mode / blocked storage — fall back to in-memory only.
            setToken(generate());
        }
    }, []);
    return token;
}
